import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  Checkbox,
  EditableTableCell,
  EditableTableContainer,
  IconButton,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@/shared/ui';
import InventoryItemPicker from '@/modules/inventory/components/InventoryItemPicker';
import type { InventoryItem } from '@/modules/inventory/api/inventory.api';
import type { MaterialLineFormRow } from '../types/treatmentCatalog.types';

interface MaterialLinesEditorProps {
  lines: MaterialLineFormRow[];
  onChange: (lines: MaterialLineFormRow[]) => void;
  disabled?: boolean;
}

const createEmptyLine = (): MaterialLineFormRow => ({
  key: `${Date.now()}-${Math.random()}`,
  inventoryItemId: '',
  item: null,
  quantity: '1',
  required: true,
});

/**
 * Editable BOM table for treatment catalog (and plan-item override).
 */
const MaterialLinesEditor: React.FC<MaterialLinesEditorProps> = ({
  lines,
  onChange,
  disabled = false,
}) => {
  const { t } = useTranslation('treatment');
  const [rows, setRows] = useState<MaterialLineFormRow[]>(
    lines.length > 0 ? lines : []
  );

  useEffect(() => {
    setRows(lines);
  }, [lines]);

  const commit = (next: MaterialLineFormRow[]): void => {
    setRows(next);
    onChange(next);
  };

  const handleAdd = (): void => {
    commit([...rows, createEmptyLine()]);
  };

  const handleRemove = (key: string): void => {
    commit(rows.filter((row) => row.key !== key));
  };

  const patchRow = (key: string, patch: Partial<MaterialLineFormRow>): void => {
    commit(rows.map((row) => (row.key === key ? { ...row, ...patch } : row)));
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle2">{t('fields.materialLines')}</Typography>
        {!disabled ? (
          <Button
            intent="ghost"
            size="tiny"
            startIcon={<AddIcon />}
            onClick={handleAdd}
          >
            {t('actions.addMaterialLine')}
          </Button>
        ) : null}
      </Box>
      <EditableTableContainer
        density="comfortable"
        elevation={0}
        sx={{ boxShadow: 'none', border: 1, borderColor: 'divider' }}
      >
        <TableHead>
          <TableRow>
            <TableCell width={280}>{t('fields.inventoryItem')}</TableCell>
            <TableCell width={100} align="right">
              {t('fields.materialQty')}
            </TableCell>
            <TableCell width={80} align="center">
              {t('fields.materialRequired')}
            </TableCell>
            {!disabled ? (
              <TableCell align="center" width={48}>
                {t('table.actions')}
              </TableCell>
            ) : null}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={disabled ? 3 : 4} align="center">
                <Typography variant="body2" color="text.secondary">
                  {t('empty.noMaterialLines')}
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row) => (
              <TableRow key={row.key}>
                <EditableTableCell>
                  <InventoryItemPicker
                    embedded
                    value={row.item}
                    disabled={disabled}
                    onChange={(item: InventoryItem | null) =>
                      patchRow(row.key, {
                        item,
                        inventoryItemId: item?._id ?? '',
                      })
                    }
                  />
                </EditableTableCell>
                <EditableTableCell inputAlign="right">
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    value={row.quantity}
                    disabled={disabled}
                    onChange={(e) => patchRow(row.key, { quantity: e.target.value })}
                    slotProps={{ htmlInput: { min: 0, step: 'any' } }}
                  />
                </EditableTableCell>
                <TableCell align="center">
                  <Checkbox
                    size="small"
                    checked={row.required}
                    disabled={disabled}
                    onChange={(e) => patchRow(row.key, { required: e.target.checked })}
                    inputProps={{ 'aria-label': t('fields.requiredShort') }}
                  />
                </TableCell>
                {!disabled ? (
                <TableCell align="center">
                  <IconButton
                    size="small"
                    onClick={() => handleRemove(row.key)}
                    aria-label={t('common:delete', { defaultValue: 'common:delete' })}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
                ) : null}
              </TableRow>
            ))
          )}
        </TableBody>
      </EditableTableContainer>
    </Box>
  );
};

export default MaterialLinesEditor;

export { createEmptyLine as createEmptyMaterialLine };
